import React from 'react';
import { Typography, Alert, Divider, Card } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import {TransactionRecordDTO} from '@/shared/api/model';

const { Title, Text } = Typography;

// Format currency
const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-TZ', {
        style: 'decimal',
        maximumFractionDigits: 0
    }).format(amount) + ' TZS';
};

interface EligibilityData {
    isGovernmentEmployee?: boolean;
    grossSalary?: number;
    netSalary?: number;
    acceptTerms?: boolean;
}

interface UpdatedCompleteRegistrationStepProps {
    registrationData: Partial<any & EligibilityData>;
    transactionRecord: TransactionRecordDTO | null;
    dark?: boolean;
}

const UpdatedCompleteRegistrationStep: React.FC<UpdatedCompleteRegistrationStepProps> = ({
                                                                                             registrationData,
                                                                                             transactionRecord,
                                                                                             dark
                                                                                         }) => {
    // Calculate eligibility status for display
    const meetsRequirements = registrationData.netSalary && registrationData.grossSalary ?
        (registrationData.netSalary >= 30000 &&
            (registrationData.netSalary - 30000) >= (registrationData.grossSalary / 3)) : false;

    return (
        <div className="space-y-6">
            <Alert
                message={
                    <span className="font-bold">Registration Completed Successfully</span>
                }
                description="Thank you for completing your registration with Hazina SACCOS. Your information has been submitted for processing."
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                className="mb-4"
            />

            <Card className={`${dark ? 'bg-gray-800 text-white' : 'bg-white'}`}>
                <Title level={4} className={dark ? 'text-white' : ''}>Registration Summary</Title>

                <div className="grid grid-cols-1 gap-4 mt-4">
                    <div>
                        <Title level={5} className={dark ? 'text-white' : ''}>Personal Information</Title>
                        <ul className="list-none space-y-2">
                            <li><Text strong className={dark ? 'text-white' : ''}>Name:</Text> {registrationData.name || 'N/A'}</li>
                            <li><Text strong className={dark ? 'text-white' : ''}>Phone Number:</Text> {registrationData.phoneNumber || 'N/A'}</li>
                            <li><Text strong className={dark ? 'text-white' : ''}>Email:</Text> {registrationData.email || 'N/A'}</li>
                            <li><Text strong className={dark ? 'text-white' : ''}>Gender:</Text> {registrationData.gender === 'M' ? 'Male' : registrationData.gender === 'F' ? 'Female' : 'N/A'}</li>
                            <li><Text strong className={dark ? 'text-white' : ''}>Date of Birth:</Text> {registrationData.dateOfBirth || 'N/A'}</li>
                        </ul>
                    </div>
                </div>

                <Divider className={dark ? 'border-gray-700' : 'border-gray-200'} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Title level={5} className={dark ? 'text-white' : ''}>Eligibility Information</Title>
                        <ul className="list-none space-y-2">
                            <li>
                                <Text strong className={dark ? 'text-white' : ''}>Government Employee:</Text> {' '}
                                {registrationData.isGovernmentEmployee ? 'Yes' : 'No'}
                            </li>
                            <li>
                                <Text strong className={dark ? 'text-white' : ''}>Gross Salary:</Text> {' '}
                                {formatCurrency(registrationData.grossSalary)}
                            </li>
                            <li>
                                <Text strong className={dark ? 'text-white' : ''}>Net Salary:</Text> {' '}
                                {formatCurrency(registrationData.netSalary)}
                            </li>
                            <li>
                                <Text strong className={dark ? 'text-white' : ''}>Eligibility Status:</Text> {' '}
                                <Text type={meetsRequirements ? "success" : "warning"}>
                                    {meetsRequirements ? 'Fully Eligible' : 'Subject to Review'}
                                </Text>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <Title level={5} className={dark ? 'text-white' : ''}>Payment Information</Title>
                        <ul className="list-none space-y-2">
                            <li><Text strong className={dark ? 'text-white' : ''}>Payment Reference:</Text> {registrationData.paymentReference || transactionRecord?.id || 'N/A'}</li>
                            <li><Text strong className={dark ? 'text-white' : ''}>Transaction Status:</Text> {transactionRecord?.trxStatus || 'N/A'}</li>
                            <li><Text strong className={dark ? 'text-white' : ''}>Initial Deposit:</Text> {formatCurrency(transactionRecord?.amount || registrationData.amount)}</li>
                            <li><Text strong className={dark ? 'text-white' : ''}>Payment Method:</Text> {registrationData.fspType || 'N/A'}</li>
                        </ul>
                    </div>
                </div>

                {!meetsRequirements && (
                    <Alert
                        message="Eligibility Review Required"
                        description="Based on the provided information, your application requires additional review by Hazina SACCOS. You will be notified of the decision via email or phone."
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        className="mt-6"
                    />
                )}

                <Alert
                    message="Next Steps"
                    description={
                        <ul className="list-disc pl-5 mt-2">
                            <li>You will receive a confirmation email with your registration details.</li>
                            <li>For bank transfers, please complete your payment within 7 days to activate your account.</li>
                            <li>Your application will be reviewed by Hazina SACCOS within 3-5 business days.</li>
                            <li>For any inquiries, contact Hazina SACCOS customer support.</li>
                        </ul>
                    }
                    type="info"
                    showIcon
                    className="mt-6"
                />
            </Card>
        </div>
    );
};

export default UpdatedCompleteRegistrationStep;